import React, { useState, useEffect, useRef } from 'react';
import { Search, User, Users, ChevronDown, LogOut, Shield } from 'lucide-react';
import { adminImpersonationService } from '../utils/adminImpersonationService';
import { logger } from '../utils/logger';

/**
 * AdminAccountSwitcher Component
 * Dropdown for super admin to search and switch to any user account
 */
const AdminAccountSwitcher = ({ currentUser, onSwitchAccount }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const dropdownRef = useRef(null);

  // Check if user is super admin
  const isSuperAdmin = adminImpersonationService.isSuperAdmin(currentUser);
  const impersonationState = adminImpersonationService.getImpersonationState();

  // Load users when dropdown opens
  useEffect(() => {
    const loadUsers = async () => {
      if (!isOpen || users.length > 0) return;

      try {
        setLoading(true);
        const allUsers = await adminImpersonationService.getAllUsers();
        setUsers(allUsers);
        setFilteredUsers(allUsers);
      } catch (error) {
        logger.error('Failed to load users for switcher', { error: error.message });
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      loadUsers();
    }
  }, [isOpen, users.length]);

  // Filter users based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = users.filter(user =>
      user.name.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query) ||
      user.company?.toLowerCase().includes(query) ||
      user.specialty?.toLowerCase().includes(query)
    );
    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSwitchToUser = async (user) => {
    try {
      logger.info('Switching to user account', { userId: user.id, email: user.email });
      await onSwitchAccount(user.id);
      setIsOpen(false);
      setSearchQuery('');
    } catch (error) {
      logger.error('Failed to switch account', { error: error.message });
      alert('Failed to switch account. Please try again.');
    }
  };

  const handleExitImpersonation = async () => {
    try {
      logger.info('Exiting impersonation');
      await onSwitchAccount(null); // null means exit impersonation
      setIsOpen(false);
      setSearchQuery('');
    } catch (error) {
      logger.error('Failed to exit impersonation', { error: error.message });
      alert('Failed to exit impersonation. Please try again.');
    }
  };

  if (!isSuperAdmin) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Switcher Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors shadow-md"
        title="Admin: Switch Account"
      >
        <Shield className="w-4 h-4" />
        <span className="text-sm font-medium">
          {impersonationState ? 'Viewing As' : 'Admin'}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-screen max-w-[calc(100vw-2rem)] sm:w-96 sm:max-w-96 md:top-auto md:bottom-full md:right-auto md:left-0 md:mt-0 md:mb-2 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 max-h-[600px] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 bg-purple-50">
            <div className="flex items-center space-x-2 mb-2">
              <Shield className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-gray-800">Admin Account Switcher</h3>
            </div>
            <p className="text-xs text-gray-600">
              Switch to any user account for customer support
            </p>
          </div>

          {/* Currently Viewing */}
          {impersonationState && (
            <div className="p-3 bg-amber-50 border-b border-amber-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4 text-amber-600" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">
                      Currently viewing: {currentUser?.first_name} {currentUser?.last_name}
                    </p>
                    <p className="text-xs text-amber-700">{currentUser?.email}</p>
                  </div>
                </div>
                <button
                  onClick={handleExitImpersonation}
                  className="flex items-center space-x-1 px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white text-xs rounded transition-colors"
                >
                  <LogOut className="w-3 h-3" />
                  <span>Exit</span>
                </button>
              </div>
            </div>
          )}

          {/* Search */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm bg-white text-gray-900 placeholder-gray-400"
                autoFocus
              />
            </div>
          </div>

          {/* User List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Loading users...</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                  {searchQuery ? 'No users found' : 'No users available'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredUsers.map((user) => {
                  const isCurrentUser = user.id === currentUser?.id;
                  return (
                    <button
                      key={user.id}
                      onClick={() => !isCurrentUser && handleSwitchToUser(user)}
                      disabled={isCurrentUser}
                      className={`w-full p-3 text-left transition-colors ${
                        isCurrentUser
                          ? 'bg-purple-50 cursor-default'
                          : 'hover:bg-gray-50 cursor-pointer'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          user.userType === 'customer'
                            ? 'bg-blue-100 text-blue-600'
                            : user.userType === 'sales'
                            ? 'bg-green-100 text-green-600'
                            : user.userType === 'office_staff'
                            ? 'bg-purple-100 text-purple-600'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          <User className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <p className="font-medium text-gray-900 truncate">
                              {user.displayLabel}
                            </p>
                            {isCurrentUser && (
                              <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                                Current
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                          <p className="text-xs text-gray-400 truncate">
                            {user.secondaryLabel}
                          </p>
                        </div>
                        <div className={`text-xs px-2 py-1 rounded ${
                          user.userType === 'customer'
                            ? 'bg-blue-100 text-blue-700'
                            : user.userType === 'sales'
                            ? 'bg-green-100 text-green-700'
                            : user.userType === 'office_staff'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {user.userType === 'customer' ? 'Provider' :
                           user.userType === 'sales' ? 'Sales' :
                           user.userType === 'office_staff' ? 'Office' :
                           user.userType || 'Unknown'}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500 text-center">
              {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} available
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAccountSwitcher;
